import { FormField, FormRenderTypes } from '../../Shared/Components/FormRenderer';
import { FormRenderGroupTypes } from '../../Shared/Components/FormRenderer/FormRenderGroup.types';
import { Reservation, ReservationPartyDraft } from '../Types/reservationTypes';

type ReservationPartyDraftPrefix =
  | 'applicantPartyDraft'
  | 'invoiceBuyerPartyDraft'
  | 'fundingPartyDraft';

function getSharedPartyContactFields(
  prefix: ReservationPartyDraftPrefix,
  title: string,
  draft: ReservationPartyDraft
): FormField<Reservation>[] {
  return [
    {
      name: `${prefix}.email` as never,
      label: `${title} - email`,
      type: FormRenderTypes.input,
      value: draft.email,
      group: FormRenderGroupTypes.basic,
    },
    {
      name: `${prefix}.phone` as never,
      label: `${title} - telefon`,
      type: FormRenderTypes.input,
      value: draft.phone,
      group: FormRenderGroupTypes.basic,
    },
    {
      name: `${prefix}.street` as never,
      label: `${title} - ulica`,
      type: FormRenderTypes.input,
      value: draft.street,
      group: FormRenderGroupTypes.basic,
    },
    {
      name: `${prefix}.city` as never,
      label: `${title} - miasto`,
      type: FormRenderTypes.input,
      value: draft.city,
      group: FormRenderGroupTypes.basic,
    },
    {
      name: `${prefix}.postalCode` as never,
      label: `${title} - kod pocztowy`,
      type: FormRenderTypes.input,
      value: draft.postalCode,
      group: FormRenderGroupTypes.basic,
    },
    {
      name: `${prefix}.countryCode` as never,
      label: `${title} - kraj (ISO)`,
      type: FormRenderTypes.input,
      value: draft.countryCode,
      group: FormRenderGroupTypes.basic,
      placeholder: 'PL',
    },
  ];
}

function getOrganizationPartyDraftFields(
  prefix: ReservationPartyDraftPrefix,
  title: string,
  draft: ReservationPartyDraft
): FormField<Reservation>[] {
  return [
    {
      name: `${prefix}.organizationName` as never,
      label: `${title} - nazwa organizacji`,
      type: FormRenderTypes.input,
      value: draft.organizationName,
      group: FormRenderGroupTypes.basic,
    },
    {
      name: `${prefix}.taxId` as never,
      label: `${title} - NIP`,
      type: FormRenderTypes.input,
      value: draft.taxId,
      group: FormRenderGroupTypes.basic,
    },
    {
      name: `${prefix}.regon` as never,
      label: `${title} - REGON`,
      type: FormRenderTypes.input,
      value: draft.regon,
      group: FormRenderGroupTypes.basic,
    },
    ...getSharedPartyContactFields(prefix, title, draft),
  ];
}

function getIndividualPartyDraftFields(
  prefix: ReservationPartyDraftPrefix,
  title: string,
  draft: ReservationPartyDraft
): FormField<Reservation>[] {
  return [
    {
      name: `${prefix}.firstName` as never,
      label: `${title} - imię`,
      type: FormRenderTypes.input,
      value: draft.firstName,
      group: FormRenderGroupTypes.basic,
    },
    {
      name: `${prefix}.lastName` as never,
      label: `${title} - nazwisko`,
      type: FormRenderTypes.input,
      value: draft.lastName,
      group: FormRenderGroupTypes.basic,
    },
    ...getSharedPartyContactFields(prefix, title, draft),
  ];
}

export function getReservationPartyDraftFields(
  prefix: ReservationPartyDraftPrefix,
  title: string,
  draft: ReservationPartyDraft | undefined
): FormField<Reservation>[] {
  const fields: FormField<Reservation>[] = [
    {
      name: `${prefix}.partyType` as never,
      label: `${title} - typ strony`,
      type: FormRenderTypes.radio,
      value: draft?.partyType ?? '',
      group: FormRenderGroupTypes.basic,
      options: [
        { label: 'Osoba prywatna', value: 'individual' },
        { label: 'Organizacja', value: 'organization' },
      ],
    },
  ];

  if (!draft?.partyType) {
    return fields;
  }

  if (draft.partyType === 'organization') {
    return [...fields, ...getOrganizationPartyDraftFields(prefix, title, draft)];
  }

  return [...fields, ...getIndividualPartyDraftFields(prefix, title, draft)];
}
